import React, { useState, useEffect } from 'react'
import { api } from '@/services/api'
import { Modal } from '@/components/common'

interface AttributionModalProps {
  show: boolean
  onClose: () => void
  onConnectedChange?: (connected: boolean) => void
}

export const AttributionModal: React.FC<AttributionModalProps> = ({ show, onClose, onConnectedChange }) => {
  const [apiKey, setApiKey] = useState('')
  const [connected, setConnected] = useState(false)
  const [keyId, setKeyId] = useState<string>()
  const [clientName, setClientName] = useState<string>()
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ ok: boolean; detail: string } | null>(null)

  // Check credential status when modal opens
  useEffect(() => {
    if (show) {
      checkCredential()
      setTestResult(null)
      if (!connected) {
        setApiKey('')
      }
    }
  }, [show])

  const checkCredential = async () => {
    try {
      const resp = await api.attribution.getCredentialExists()
      setConnected(resp.exists)
      if (resp.exists) {
        setKeyId(resp.key_id)
        setClientName(resp.client_name)
      }
    } catch (e) {
      console.error('Failed to check attribution credential:', e)
    }
  }

  const handleTestConnection = async () => {
    if (!apiKey.trim()) {
      setTestResult({ ok: false, detail: 'Please enter API Key' })
      return
    }
    setTesting(true)
    setTestResult(null)
    try {
      const resp = await api.attribution.testConnection(apiKey.trim())
      setTestResult({ ok: resp.ok, detail: resp.detail })
      if (resp.ok) {
        setKeyId(resp.key_id)
        setClientName(resp.client_name)
      }
    } catch (e) {
      setTestResult({ ok: false, detail: 'Connection failed. Please check your network settings.' })
    } finally {
      setTesting(false)
    }
  }

  const handleSave = async () => {
    if (!apiKey.trim()) {
      setTestResult({ ok: false, detail: 'Please enter API Key' })
      return
    }
    try {
      const resp = await api.attribution.saveCredential(apiKey.trim())
      if (resp.message === 'Saved successfully') {
        setConnected(true)
        onConnectedChange?.(true)
        handleClose()
      }
    } catch (e) {
      setTestResult({ ok: false, detail: 'Failed to save credential' })
    }
  }

  const handleDelete = async () => {
    try {
      await api.attribution.deleteCredential()
      setConnected(false)
      setKeyId(undefined)
      setClientName(undefined)
      setApiKey('')
      setTestResult(null)
      onConnectedChange?.(false)
      handleClose()
    } catch (e) {
      console.error('Failed to delete attribution credential:', e)
    }
  }

  const handleClose = () => {
    setApiKey('')
    setTestResult(null)
    onClose()
  }

  return (
    <Modal show={show} onClose={handleClose} title="Attribution Integration" size="md">
      <div className="attribution-modal">
        <p className="modal-desc">Configure Attribution API to track content performance and traffic sources</p>

        {connected ? (
          <>
            {/* Connected state */}
            <div className="attribution-modal__connected">
              <div className="attribution-modal__connected-header">
                <svg className="icon attribution-modal__connected-icon">
                  <use href="#icon-check-circle"></use>
                </svg>
                <div>
                  <div className="attribution-modal__connected-title">Connected</div>
                  <div className="attribution-modal__connected-subtitle">Attribution API successfully connected</div>
                </div>
              </div>
              {keyId && (
                <div className="attribution-modal__meta">
                  Key ID: <code className="attribution-modal__code">{keyId}</code>
                </div>
              )}
              {clientName && (
                <div className="attribution-modal__meta">
                  Client: <span>{clientName}</span>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <span className="modal-selected-count">Connected</span>
              <div className="modal-actions">
                <button className="btn btn-secondary btn-sm" onClick={handleClose}>Close</button>
                <button className="btn btn-danger btn-sm" onClick={handleDelete}>Delete Credential</button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Not connected state */}
            <div className="attribution-modal__field">
              <label className="attribution-modal__label">Attribution API Key</label>
              <input
                type="password"
                className="publish-url-input"
                placeholder="Enter your Attribution API Key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
              />
            </div>

            {testResult && (
              <div
                className={`attribution-modal__alert ${testResult.ok ? 'attribution-modal__alert--ok' : 'attribution-modal__alert--err'}`}
              >
                <div className="attribution-modal__alert-row">
                  {testResult.ok ? (
                    <svg className="icon attribution-modal__alert-icon attribution-modal__alert-icon--ok">
                      <use href="#icon-check-circle"></use>
                    </svg>
                  ) : (
                    <svg className="icon attribution-modal__alert-icon attribution-modal__alert-icon--err">
                      <use href="#icon-x-circle"></use>
                    </svg>
                  )}
                  <span>{testResult.detail}</span>
                </div>
                {testResult.ok && keyId && (
                  <div className="attribution-modal__alert-meta">
                    Key ID: {keyId}
                  </div>
                )}
              </div>
            )}

            <div className="modal-footer modal-footer--right">
              <div className="modal-actions">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={handleTestConnection}
                  disabled={testing || !apiKey.trim()}
                >
                  {testing ? 'Testing...' : 'Test Connection'}
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleSave}
                  disabled={!testResult?.ok}
                >
                  Save
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
